// Shared filtering / sorting logic for the merchant tables (global "All Merchants"
// and the ext-admin "Merchant Referrals" page). Both screens receive raw Profiles
// documents, so a single set of accessors works for either one — the only
// difference is that the ext-admin payload also carries a `referredBy` object
// (type/name/code) while the global payload only has the raw `refBy` code.

export const DEFAULT_MERCHANT_FILTERS = {
  search: "",
  referrer: "all", // "all" | "none" | <ref code>
  referrerType: "all", // "all" | "admin" | "agent"
  plan: "all", // "all" | "free" | <plan name>
  subscription: "all", // "all" | "active" | "expired" | "none"
  wallet: "all", // "all" | "wallet" | "none"
  state: "all", // "all" | <state name>
  loginActivity: "all", // "all" | "never" | "7" | "30" | "inactive30" | "inactive90"
  joinedFrom: "",
  joinedTo: "",
  lastLoginFrom: "",
  lastLoginTo: "",
  sortBy: "createdAt", // "createdAt" | "lastLogin" | "storeName" | "plan"
  sortDir: "desc", // "asc" | "desc"
};

// Fields that are genuinely "filters" — used for the active-filter badge and by
// Clear all. `search`, `sortBy` and `sortDir` are deliberately excluded.
const FILTER_KEYS = [
  "referrer",
  "referrerType",
  "plan",
  "subscription",
  "wallet",
  "state",
  "loginActivity",
  "joinedFrom",
  "joinedTo",
  "lastLoginFrom",
  "lastLoginTo",
];

export const countActiveFilters = (filters) =>
  FILTER_KEYS.filter((k) => {
    const v = filters[k];
    return v && v !== "all";
  }).length;

export const clearMerchantFilters = (filters) => ({
  ...filters,
  ...FILTER_KEYS.reduce(
    (acc, k) => ({ ...acc, [k]: DEFAULT_MERCHANT_FILTERS[k] }),
    {},
  ),
});

// ---------------------------------------------------------------- accessors

export const getReferrerCode = (m) =>
  (m.referredBy && m.referredBy.code) || m.refBy || "";

export const getReferrerLabel = (m) =>
  (m.referredBy && m.referredBy.name) || m.refBy || "";

export const getReferrerType = (m) => (m.referredBy && m.referredBy.type) || "";

export const getStoreName = (m) =>
  (m.storeInfo && m.storeInfo.storeName) || "";

export const getState = (m) =>
  (m.location && m.location.state) ||
  (m.storeInfo && m.storeInfo.storeState) ||
  "";

// createdAt is an ISO string, lastLogin a Date and subscriptionEnd a raw epoch
// number — normalise all three to millis (null when absent/unparseable).
export const toTime = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const t = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

const startOfDay = (dateStr) => {
  if (!dateStr) return null;
  const t = new Date(`${dateStr}T00:00:00`).getTime();
  return Number.isNaN(t) ? null : t;
};

const endOfDay = (dateStr) => {
  if (!dateStr) return null;
  const t = new Date(`${dateStr}T23:59:59.999`).getTime();
  return Number.isNaN(t) ? null : t;
};

const inRange = (time, from, to) => {
  const start = startOfDay(from);
  const end = endOfDay(to);
  if (start === null && end === null) return true;
  if (time === null) return false; // no date on record → excluded by a range
  if (start !== null && time < start) return false;
  if (end !== null && time > end) return false;
  return true;
};

const DAY = 24 * 60 * 60 * 1000;

const matchesLoginActivity = (lastLogin, activity) => {
  if (activity === "all") return true;
  if (activity === "never") return lastLogin === null;
  if (lastLogin === null) return false;
  const age = Date.now() - lastLogin;
  switch (activity) {
    case "7":
      return age <= 7 * DAY;
    case "30":
      return age <= 30 * DAY;
    case "inactive30":
      return age > 30 * DAY;
    case "inactive90":
      return age > 90 * DAY;
    default:
      return true;
  }
};

const matchesSubscription = (m, subscription) => {
  if (subscription === "all") return true;
  const end = toTime(m.subscriptionEnd) || 0;
  if (subscription === "none") return !end;
  if (subscription === "active") return !!end && Date.now() < end;
  if (subscription === "expired") return !!end && Date.now() >= end;
  return true;
};

// ------------------------------------------------------------ option lists

// Builds the dropdown option lists from whatever is actually present in the
// data, so the selects never offer a value that matches zero merchants.
export const buildFilterOptions = (merchants) => {
  const referrerMap = new Map();
  const plans = new Set();
  const states = new Set();
  let noReferrerCount = 0;

  merchants.forEach((m) => {
    const code = getReferrerCode(m);
    if (code) {
      const existing = referrerMap.get(code);
      if (existing) {
        existing.count += 1;
        // A merchant enriched with `referredBy` carries the referrer's real
        // name; one that only has the raw `refBy` code doesn't. Whichever we
        // see first, prefer the human-readable label.
        const label = getReferrerLabel(m);
        if (existing.label === code && label && label !== code)
          existing.label = label;
        if (!existing.type) existing.type = getReferrerType(m);
      } else
        referrerMap.set(code, {
          value: code,
          label: getReferrerLabel(m) || code,
          code,
          type: getReferrerType(m),
          count: 1,
        });
    } else {
      noReferrerCount += 1;
    }

    if (m.plan) plans.add(m.plan);
    const state = getState(m);
    if (state) states.add(state);
  });

  return {
    referrers: [...referrerMap.values()].sort((a, b) =>
      a.label.localeCompare(b.label),
    ),
    noReferrerCount,
    plans: [...plans].sort((a, b) => a.localeCompare(b)),
    states: [...states].sort((a, b) => a.localeCompare(b)),
  };
};

// ---------------------------------------------------------------- filtering

export const applyMerchantFilters = (merchants, filters) => {
  const f = { ...DEFAULT_MERCHANT_FILTERS, ...filters };
  const q = f.search.trim().toLowerCase();

  const filtered = (merchants || []).filter((m) => {
    if (q) {
      const haystack = [
        m.email,
        getStoreName(m),
        m.fullname,
        m.phone,
        getReferrerLabel(m),
        getReferrerCode(m),
      ];
      if (!haystack.some((v) => v && String(v).toLowerCase().includes(q)))
        return false;
    }

    if (f.referrer !== "all") {
      const code = getReferrerCode(m);
      if (f.referrer === "none") {
        if (code) return false;
      } else if (code !== f.referrer) return false;
    }

    if (f.referrerType !== "all" && getReferrerType(m) !== f.referrerType)
      return false;

    if (f.plan !== "all") {
      if (f.plan === "free") {
        if (m.plan) return false;
      } else if (m.plan !== f.plan) return false;
    }

    if (!matchesSubscription(m, f.subscription)) return false;

    if (f.wallet !== "all") {
      const hasWallet = !!m.wallet;
      if (f.wallet === "wallet" && !hasWallet) return false;
      if (f.wallet === "none" && hasWallet) return false;
    }

    if (f.state !== "all" && getState(m) !== f.state) return false;

    const lastLogin = toTime(m.lastLogin);
    if (!matchesLoginActivity(lastLogin, f.loginActivity)) return false;
    if (!inRange(lastLogin, f.lastLoginFrom, f.lastLoginTo)) return false;
    if (!inRange(toTime(m.createdAt), f.joinedFrom, f.joinedTo)) return false;

    return true;
  });

  const dir = f.sortDir === "asc" ? 1 : -1;
  const sorted = [...filtered].sort((a, b) => {
    switch (f.sortBy) {
      case "storeName":
        return (
          dir *
          (getStoreName(a) || a.email || "").localeCompare(
            getStoreName(b) || b.email || "",
          )
        );
      case "plan":
        return dir * (a.plan || "").localeCompare(b.plan || "");
      case "lastLogin": {
        // Never-logged-in merchants always sort last, in either direction.
        const at = toTime(a.lastLogin);
        const bt = toTime(b.lastLogin);
        if (at === null && bt === null) return 0;
        if (at === null) return 1;
        if (bt === null) return -1;
        return dir * (at - bt);
      }
      case "createdAt":
      default: {
        const at = toTime(a.createdAt) || 0;
        const bt = toTime(b.createdAt) || 0;
        return dir * (at - bt);
      }
    }
  });

  return sorted;
};
